<template>
  <v-container fluid v-resize="onResize">
    <v-system-bar>
      🥇
      <v-breadcrumbs :items="breadscrumbs()">
        <template v-slot:divider>
          <v-icon>mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>
      <v-spacer></v-spacer>
      <BaseItemProperty :page-properties="editedItem.page_properties"></BaseItemProperty>
    </v-system-bar>
    <v-card :height="windowSize.y - subtractHeight">
      <v-progress-linear :active="loading" :indeterminate="loading" absolute top>
      </v-progress-linear>
      <v-sheet>
        <v-card-title>
          <v-card class="mr-2 px-3">
            <a v-if="editedItem.notion_id" :href="getNotionLink(editedItem.notion_id)" target="_blank">
              <span class="material-icons material-icon-size">🥇</span>
            </a>
            {{ editedItem.title }}
          </v-card>
          <v-card class="mx-2 px-3">
            <!--            Product:-->
            <a v-if="editedItem.variant_notion_id" :href="getNotionLink(editedItem.variant_notion_id)" target="_blank">
              <span class="material-icons material-icon-size">🎛</span>
            </a>
            <router-link class="ml-3" link outlined
                         :to="{name:'ProductItem', params: { itemUid: editedItem.variant_uid}}">
              {{ editedItem.variant_title }}
            </router-link>
          </v-card>
          <v-card class="mx-2 px-3">
            <!--            Customer🤴:-->
            <a v-if="editedItem.customer_notion_id" :href="getNotionLink(editedItem.customer_notion_id)"
               target="_blank">
              <span class="material-icons material-icon-size">🤴</span>
            </a>
            <router-link class="ml-3" link outlined
                         :to="{name:'CustomerItem', params: { itemUid: editedItem.customer_uid}}">
              {{ editedItem.customer_title }}
            </router-link>
          </v-card>
        </v-card-title>
        <v-card-text class="pt-3">
          <v-row class="mb-n8">
            <v-chip class="mx-1">{{$t('columns.status')}}: {{editedItem.status_delivery}}</v-chip>
            <v-chip class="mx-1">{{$t('columns.customer_order_number')}}: {{editedItem.customer_order_number}}</v-chip>
            <v-chip class="mx-1">{{$t('columns.date_receipt')}}: {{editedItem.date_receipt}}</v-chip>
            <v-chip class="mx-1">{{$t('columns.editors') + ': '}}
              <span class="mx-1" v-for="editor of editedItem.editors_list" :key="editor">{{editor}}</span>
            </v-chip>
            <v-chip class="mx-1">{{$t('columns.smd')}}: {{editedItem.smd}}</v-chip>
            <v-chip class="mx-1">{{$t('columns.tht')}}: {{editedItem.tht}}</v-chip>
            <v-chip class="mx-1">{{$t('columns.extra')}}: {{editedItem.extra}}</v-chip>
            <v-spacer></v-spacer>
            <v-text-field
                v-model.number="editedItem.sn_from"
                :label="$t('columns.sn_from')"
                dense
                type="number"
                outlined
                class="no-spin mx-1"
                @input="onValidate(); editedItem.sn_from = $event !== '' ? $event : null"
                :readonly="editedItem.page_properties && editedItem.page_properties.lock_page"
                :filled="editedItem.page_properties && editedItem.page_properties.lock_page"
            >
            </v-text-field>
          </v-row>
        </v-card-text>
      </v-sheet>
      <v-divider></v-divider>
      <v-row class="mt-1">
        <v-col xl="8" lg="8" md="12" sm="12" xs="12">
          <v-sheet :height="windowSize.y - subtractHeight - 180" class="overflow-y-auto">
            <BaseCardList
                v-if="!!editedItem && editedItem.deliveries && !!editedItem.page_properties"
                :item-list="editedItem.deliveries"
                :common="editedItemCommon"
                :get-card-title-color="getDeliveryTitleColor"
                :get-item-index-color="getItemIndexColor"
                :delete-restore-event="onValidate"
                :default-item="newDeliveryItem"
                :correct-default-item="addDelivery"
                :enable-add-row="enableAddDelivery"
                xl="12" lg="12" md="12" sm="12" xs="12"
                :disableDrag="true"
                :disable-item-edit="disableDeliveryItemEdit"
                :show-deleted="editedItem.page_properties.show_deleted"
                @change="onValidate()"
            >
              <template v-slot:top="">
                <v-toolbar height="36" class="mb-5">
                  <v-row no-gutters class="flex-nowrap">
                    🤑
                    <v-divider vertical class="mx-2"></v-divider>
                    <span class="font-weight-bold h4 text-center py-0">{{ $t('delivery') }}</span>
                    <v-spacer></v-spacer>
                    <v-chip small outlined>{{ $t('columns.lot_size') }}: {{ editedItem.needed_quantity }} / {{ editedItem.lot_size }}</v-chip>
                    <v-spacer></v-spacer>
                    <v-chip small outlined>{{ $t('columns.shipped') }}: {{ editedItem.shipped }}</v-chip>
                    <v-spacer></v-spacer>
                    <v-chip small outlined>{{ $t('columns.open') }}: {{ editedItem.open }}</v-chip>
                    <v-spacer></v-spacer>
                    <v-chip small outlined>{{ $t('columns.shippable') }}: {{ editedItem.shippable }}</v-chip>
                  </v-row>
                </v-toolbar>
              </template>
              <template v-slot:bar="{item, index, common}">
                <v-chip small outlined color="warning">
                  <v-checkbox
                      v-model="item.make_to_stock"
                      color="warning"
                      :readonly="readOnlyDelivery(item)"
                      @change="onValidate()"
                  >
                    <template v-slot:label>
                      <small class="mx-0">{{ $t('columns.make_to_stock') }}</small>
                    </template>
                  </v-checkbox>
                </v-chip>
                <v-chip small outlined>
                  <v-checkbox
                      v-model="item.extra"
                      :readonly="readOnlyDelivery(item)"
                      @change="onValidate()"
                  >
                    <template v-slot:label>
                      <small class="mx-0">{{ $t('columns.extra') }}</small>
                    </template>
                  </v-checkbox>
                </v-chip>
                <v-chip v-if="item.sn_from != null && item.sn_to != null" small outlined
                        class="font-weight-bold mx-1">
                  S/N {{ item.sn_from }} - {{ item.sn_to }}
                </v-chip>
                <v-chip small class="font-weight-bold mx-1"
                        :color="$t('colors.status_delivery.' + (item.status_delivery))">
                  {{ $t('status_delivery.' + (item.status_delivery)) }}
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn icon :to="{name:'DeliveryItem', params: { itemUid: item.uid}}"
                >
                  <v-icon color="blue lighten-2">mdi-open-in-app</v-icon>
                </v-btn>
              </template>
              <template v-slot:body="{item, index, common}">
                <v-row no-gutters dense class="flex-nowrap pt-3" v-if="!!common && !!item">
                  <v-text-field
                      v-model="item.planned_date"
                      outlined
                      class="mx-1 mb-n3"
                      :label="$t('columns.planned_date') + ' ' + getYearWeek(item.planned_date)"
                      dense
                      type="date"
                      @input="onValidate(); item.planned_date = $event !== '' ? $event : null"
                      :readonly="readOnlyDelivery(item) || item.property_state.planned_date.readonly"
                      :filled="readOnlyDelivery(item) || item.property_state.planned_date.readonly"
                      :background-color="item.make_to_stock ? 'orange lighten-4' : null"
                      :error="item.property_state.planned_date.error"
                  ></v-text-field>
                  <v-text-field
                      v-model="item.confirmed_date"
                      outlined
                      :label="$t('columns.confirmed_date') + ' ' + getYearWeek(item.confirmed_date)"
                      dense
                      class="mx-1 mb-n4"
                      type="date"
                      @input="onValidate(); item.confirmed_date = $event !== '' ? $event : null"
                      :readonly="readOnlyDelivery(item) || item.property_state.confirmed_date.readonly"
                      :filled="readOnlyDelivery(item) || item.property_state.confirmed_date.readonly"
                      :error="item.property_state.confirmed_date.error"
                  ></v-text-field>
                  <v-text-field
                      v-model="item.originally_confirmed"
                      outlined
                      :label="$t('columns.originally_confirmed') + ' ' + getYearWeek(item.originally_confirmed)"
                      dense
                      class="mx-1 mb-n4"
                      type="date"
                      readonly
                      filled
                  ></v-text-field>
                  <v-col cols="4">
                    <v-textarea
                        v-model="item.comment"
                        outlined
                        :label="$t('columns.comment')"
                        dense
                        rows="1"
                        class="mx-1 mb-n4"
                        type="text"
                        :readonly="readOnlyDelivery(item)"
                        :filled="readOnlyDelivery(item)"
                    >
                    </v-textarea>
                  </v-col>
                </v-row>
                <v-divider></v-divider>
                <v-row no-gutters class="flex-nowrap" v-if="!!common && !!item">
                  <v-text-field
                      v-model.number="item.quantity"
                      @input="onValidate(); item.quantity = $event !== '' ? $event : null"
                      outlined
                      class="no-spin number-short mt-1 mx-1 mb-n6"
                      type="number"
                      :label="$t('columns.quantity')"
                      dense
                      :readonly="readOnlyDelivery(item) || item.property_state.quantity.readonly"
                      :filled="readOnlyDelivery(item) || item.property_state.quantity.readonly"
                      :error="item.property_state.quantity.error"
                  >
                  </v-text-field>
                  <v-text-field
                      v-model.number="item.min_smd"
                      @input="onValidate(); item.min_smd = $event !== '' ? $event : null"
                      outlined
                      class="no-spin number-short mt-1 mx-1 mb-n6"
                      type="number"
                      :label="$t('columns.min_smd')"
                      dense
                      :readonly="readOnlyDelivery(item) || item.property_state.min_smd.readonly"
                      :filled="readOnlyDelivery(item) || item.property_state.min_smd.readonly"
                      :error="item.property_state.min_smd.error"
                  >
                  </v-text-field>
                  <v-icon x-small>mdi-minus</v-icon>
                  <v-text-field
                      v-model.number="item.max_smd"
                      @input="onValidate(); item.max_smd = $event !== '' ? $event : null;"
                      outlined
                      class="no-spin number-short mt-1 mx-1 mb-n6"
                      type="number"
                      :label="$t('columns.max_smd')"
                      dense
                      :readonly="readOnlyDelivery(item) || item.property_state.max_smd.readonly"
                      :filled="readOnlyDelivery(item) || item.property_state.max_smd.readonly"
                      :error="item.property_state.max_smd.error"
                  >
                  </v-text-field>
                  <v-text-field
                      v-model.number="item.min_tht"
                      @input="onValidate(); item.min_tht = $event !== '' ? $event : null"
                      outlined
                      class="no-spin number-short mt-1 mx-1 mb-n6"
                      type="number"
                      :label="$t('columns.min_tht')"
                      dense
                      :readonly="readOnlyDelivery(item) || item.property_state.min_tht.readonly"
                      :filled="readOnlyDelivery(item) || item.property_state.min_tht.readonly"
                      :error="item.property_state.min_tht.error"
                  >
                  </v-text-field>
                  <v-icon x-small>mdi-minus</v-icon>
                  <v-text-field
                      v-model.number="item.max_tht"
                      @input="onValidate(); item.max_tht = $event !== '' ? $event : null"
                      outlined
                      class="no-spin number-short mt-1 mx-2 mb-n6"
                      type="number"
                      :label="$t('columns.max_tht')"
                      dense
                      :readonly="readOnlyDelivery(item) || item.property_state.max_tht.readonly"
                      :filled="readOnlyDelivery(item) || item.property_state.max_tht.readonly"
                      :error="item.property_state.max_tht.error"
                  >
                  </v-text-field>
                  <v-col cols="4">
                    <v-textarea
                        v-model="item.production_note"
                        outlined
                        :label="$t('columns.production_note')"
                        dense
                        rows="1"
                        class="mt-1 mx-1 mb-n6"
                        type="text"
                        :readonly="readOnlyDelivery(item)"
                        :filled="readOnlyDelivery(item)"
                    >
                    </v-textarea>
                  </v-col>
                </v-row>
              </template>
            </BaseCardList>
          </v-sheet>
        </v-col>
        <v-col xl="4" lg="4" md="10" sm="12" xs="12">
          <v-sheet :height="windowSize.y - subtractHeight - 180" class="overflow-y-auto">
            <BaseCardList
                v-if="!!editedItem && editedItem.delivery_brs && !!editedItem.page_properties"
                :item-list="editedItem.delivery_brs"
                :common="editedItemCommon"
                :enable-add-row="enableAddBatchRun"
                :get-card-title-color="getBatchRunTitleColor"
                xl="12" lg="12" md="12" sm="12" xs="12"
                :disable-drag="true"
                :disable-item-edit="disableBatchRunItemEdit"
                :show-deleted="editedItem.page_properties.show_deleted"
            >
              <template v-slot:top="">
                <v-toolbar height="36" class="mb-5">
                  <v-row no-gutters class="flex-nowrap">
                    🦸
                    <v-divider vertical class="mx-2"></v-divider>
                    <span class="font-weight-bold h4 text-center py-0">{{ $t('batch_run') }}</span>
                    <v-spacer></v-spacer>
                    <v-chip small outlined>
                      {{ $t('columns.finished_smd_tht') }}: {{ editedItem.smd_finished }} / {{
                        editedItem.tht_finished
                      }}
                    </v-chip>
                    <v-spacer></v-spacer>
                    <v-chip small outlined>
                      {{ $t('columns.batch_size_correction') }}: {{ editedItem.batch_size_correction }}
                    </v-chip>
                  </v-row>
                </v-toolbar>
              </template>
              <template v-slot:bar="{item, index, common}">
                <v-spacer></v-spacer>
                <v-btn icon :to="{name:'BatchRunItem', params: { itemUid: item.uid}}">
                  <v-icon color="blue lighten-2">mdi-open-in-app</v-icon>
                </v-btn>
              </template>
              <template v-slot:body="{item, index, common}">
                <v-row dense class="flex-nowrap" v-if="!!common && item.has_smd">
                  <v-col cols="6">
                    <v-text-field
                        v-model="item.sn_smd_from + ' - ' + item.sn_smd_to"
                        outlined
                        class="mt-1 mb-n6 ml-1"
                        label="S/N SMD"
                        background-color="orange lighten-4"
                        dense
                        filled
                        readonly
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                        v-model.number="item.plan_smd"
                        outlined
                        class="no-spin number-short mt-1 mb-n6"
                        type="number"
                        :label="$t('columns.plan_smd')"
                        background-color="orange lighten-4"
                        dense
                        :filled="lockPage"
                        :readonly="lockPage"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                        v-model.number="item.as_is_smd"
                        outlined
                        class="no-spin number-short mt-1 mb-n6 mr-1"
                        type="number"
                        :label="$t('columns.as_is_smd')"
                        background-color="orange lighten-4"
                        dense
                        filled
                        readonly
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row dense class="flex-nowrap" v-if="!!common && item.has_tht">
                  <v-col cols="6">
                    <v-text-field
                        v-model="item.sn_tht_from + ' - ' + item.sn_tht_to"
                        outlined
                        class="mt-1 mb-n6 ml-1"
                        label="S/N THT"
                        background-color="blue lighten-4"
                        dense
                        filled
                        readonly
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                        v-model.number="item.plan_tht"
                        outlined
                        class="no-spin number-short mt-1 mb-n6"
                        type="number"
                        :label="$t('columns.plan_tht')"
                        background-color="blue lighten-4"
                        dense
                        :filled="lockPage"
                        :readonly="lockPage"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                        v-model.number="item.as_is_tht"
                        outlined
                        class="no-spin number-short mt-1 mb-n6 mr-1"
                        type="number"
                        :label="$t('columns.as_is_tht')"
                        background-color="blue lighten-4"
                        dense
                        filled
                        readonly
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </template>
            </BaseCardList>
          </v-sheet>
        </v-col>
      </v-row>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text elevation="3" class="mx-1" :to="{name: 'CustomerOrderTable'}">
          {{ $t('Cancel') }}
        </v-btn>
        <v-btn
            :disabled="lockPage || editedItem.state_error"
            text elevation="3" class="mx-1" :loading="commandLoading" @click="onSave()">
          {{ $t('Save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <ConfirmDialog :confirm_request="confirm_request" :fetch-request="fetchRequest"></ConfirmDialog>
  </v-container>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import {mapFields} from 'vuex-map-fields';
import ConfirmDialog from "@/components/Base/ConfirmDialog";
import {v4 as uuidv4} from 'uuid'
import commonFunction from "@/commonFunction/commonFunction";
import BaseCardList from "@/components/Base/BaseCardList";
import BaseItemProperty from "@/components/Base/BaseItemProperty";

export default {
  name: 'CustomerOrderItem',
  props: {
    subtractHeight: {type: [Number], default: 50},
    height: {type: [Number], default: window.innerHeight},
    itemUid: {default: null},
    dto_detail_model: {type: [String], default: 'CustomerOrderDetailQueryDTO'},
    dto_save_model: {type: [String], default: 'CustomerOrderSaveCommandDTO'},
    dto_validate_model: {type: [String], default: 'CustomerOrderValidateCommandDTO'},
  },

  components: {
    ConfirmDialog,
    BaseCardList,
    BaseItemProperty,
  },

  computed: {
    ...mapGetters('CustomerOrderTable', ['getColumns', 'getStatusCustomerOrder']),
    ...mapFields('CustomerOrderTable', ['errorMessage', 'options', 'totalRecords', 'options.page',
      'editedItem', 'editedItemCommon', 'itemList', 'backupEditedItem', 'backupEditedItemCommon', 'confirm_request',
      'loading', 'commandLoading', 'selectedItemList', 'searchTermFile', 'searchTerm',
      'statusCustomerOrder']),
    lockPage() {
      return !(!!this.editedItem && !!this.editedItem.page_properties && !this.editedItem.page_properties.lock_page)
    },

  },

  methods: {
    ...mapActions('CustomerOrderTable', ['openWebSocket', 'fetchItemList', 'fetchItem', 'fetchValidateItem',
      'fetchSaveEditedItem', 'fetchActionSelectedItem', 'fetchActionItem', 'fetchRequest', 'fetchDetailRestore']),
    getNotionLink: commonFunction.getNotionLink,
    getDateOfISOWeek: commonFunction.getDateOfISOWeek,

    onValidate() {
      // this.fetchDetailRestore()
      this.fetchValidateItem(this.dto_validate_model)
    },

    onSave() {
      this.fetchSaveEditedItem(this.dto_save_model)
    },

    onResize() {
      this.windowSize = {x: window.innerWidth, y: window.innerHeight};
    },

    breadscrumbs() {
      return [
        {text: 'Home', href: '/site/',},
        {text: 'CustomerOrder', href: '/site/batch_run/customer_order',},
        {
          text: this.editedItem.title,
          href: '/site/batch_run/customer_order/' + this.$route.params.itemUid,
        },
      ]
    },

    getItemIndexColor(item) {
      if (!!item && item.state_error === true)
        return "error"
      else
        return null
    },

    enableAddDelivery() {
      return !this.lockPage && this.editedItem.needed_quantity > 0
    },

    enableAddBatchRun() {
      return false
    },

    addDelivery(newItem) {
      console.log('addDelivery', this.editedItem.needed_quantity)
      newItem.quantity = this.editedItem.needed_quantity
      this.onValidate()
    },

    readOnlyDelivery(item) {
      return this.lockPage || item.deleted
    },

    disableDeliveryItemEdit(item) {
      return this.lockPage
    },

    disableBatchRunItemEdit(item) {
      return this.lockPage
    },

    getDeliveryTitleColor() {
      return null
    },
    getBatchRunTitleColor() {
      return null
    },
    getYearWeek(date) {
      if (!!date)
        return this.$moment(date).format('GG') + '-' + this.$moment(date).format('WW')
      else
        return ''
    },
  },

  async mounted() {
    // console.log('mounted')
    await this.fetchItem({
      itemUid: this.itemUid,
      dto_detail_model: this.dto_detail_model,
    })
  },

  beforeDestroy() {
  },

  data() {
    return {
      windowSize: {x: 0, y: 0,},
      optionsDelivery: {
        page: 1,
        itemsPerPage: -1,
        sortDesc: [false, false],
        multiSort: true,
        mustSort: true,
        descending: false,
      },
      optionsBR: {
        page: 1,
        itemsPerPage: -1,
        sortBy: ['latest_date'],
        sortDesc: [false],
        multiSort: false,
        mustSort: true,
        descending: false,
      },
      newDeliveryItem: {
        deleted: false,
        uid: uuidv4(),
        planned_date: null,
        confirmed_date: null,
        original_confirmed: null,
        make_to_stock: false,
        status_delivery: 'unplanned',
        quantity: null,
        min_smd: null,
        min_tht: null,
        max_smd: null,
        max_tht: null,
        comment: null,
        production_note: null,
        state_error: true,
        property_state: {
          planned_date: {error: true, readonly: false},
          confirmed_date: {error: true, readonly: false},
          quantity: {error: true, readonly: false},
          min_smd: {error: true, readonly: false},
          max_smd: {error: true, readonly: false},
          min_tht: {error: true, readonly: false},
          max_tht: {error: true, readonly: false},
        }
      },
    }
  }
}
</script>

<style>

.no-spin input[type='number'] {
  -moz-appearance: textfield;
  /*width: 50px;*/
}

/*.year-short input[type='number'] {*/
/*  width: 5px;*/
/*}*/

/*.week-short input[type='number'] {*/
/*  width: 2px;*/
/*}*/

/*.number-short input[type='number'] {*/
/*  width: 30px;*/
/*}*/

.shadow-row.v-data-table > .v-data-table__wrapper > table {
  border-spacing: 0 16px !important;
}

.shadow-row.v-data-table > .v-data-table__wrapper > table > tbody > tr {
  box-shadow: 3px 5px 6px -4px rgba(0, 0, 0, 0.8);
}

</style>

<i18n src=".\locales.json">
</i18n>
